import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  ListItem,
  SimpleGrid,
  Spinner,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import PageHeader from "../components/PageHeader";
import PoolCardScroller from "../components/PoolCardScroller";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  getPsActiveSeason,
  getMyPsPools,
  getAllPsPools,
  createPsPool,
  joinPsPool,
} from "../api/mlbPlayoff";
import mlbPlayoffLogo from "../assets/logos/mlb-playoffs.png";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE    = "#00007f";

export default function MlbPlayoffHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [myPools,      setMyPools]      = useState([]);
  const [allPools,     setAllPools]     = useState([]);
  const [loadingPools, setLoadingPools] = useState(true);
  const [,             setPoolError]    = useState("");

  const [newPoolName,   setNewPoolName]   = useState("");
  const [newPoolDepth,  setNewPoolDepth]  = useState("");
  const [creating,      setCreating]      = useState(false);
  const [createError,   setCreateError]   = useState("");
  const [joiningPoolId, setJoiningPoolId] = useState(null);
  const [joinError,     setJoinError]     = useState("");
  const [season,        setSeason]        = useState(null);

  useEffect(() => { loadPools(); }, []);

  async function loadPools() {
    setLoadingPools(true);
    setPoolError("");
    try {
      const s = await getPsActiveSeason();
      setSeason(s);
      const [mine, all] = await Promise.all([
        getMyPsPools(s.id),
        getAllPsPools(s.id),
      ]);
      setMyPools(mine);
      setAllPools(all);
    } catch (err) {
      setPoolError(err?.response?.data?.detail || "Failed to load pools.");
      setMyPools([]);
      setAllPools([]);
    } finally {
      setLoadingPools(false);
    }
  }

  async function handleCreatePool() {
    setCreateError("");
    if (!newPoolName.trim()) { setCreateError("Pool name is required."); return; }
    setCreating(true);
    try {
      const pool = await createPsPool(season.id, {
        name: newPoolName.trim(),
        pool_depth: newPoolDepth ? parseFloat(newPoolDepth) : null,
      });
      setNewPoolName("");
      setNewPoolDepth("");
      navigate(`/mlb-playoff/current/${pool.id}/draft`);
    } catch (err) {
      setCreateError(err?.response?.data?.detail || "Failed to create pool.");
    } finally {
      setCreating(false);
    }
  }

  async function handleJoinPool(poolId) {
    setJoinError("");
    setJoiningPoolId(poolId);
    try {
      await joinPsPool(season.id, poolId);
      navigate(`/mlb-playoff/current/${poolId}/draft`);
    } catch (err) {
      setJoinError(err?.response?.data?.detail || "Failed to join pool.");
      setJoiningPoolId(null);
    }
  }

  const joinablePools = useMemo(() => {
    const myPoolIds = new Set(myPools.map((p) => p.id));
    return allPools.filter((p) => !myPoolIds.has(p.id));
  }, [myPools, allPools]);

  const poolCreationOpen = !!season?.pool_creation_open;
  const seasonStarted = !!season?.season_started;
  // Joining outlives pool creation. Creation shuts the moment any team plays;
  // a pool still takes members until every team left has started, because
  // until then there are still rosters to be drafted.
  const entriesOpen = season?.entries_open !== false;

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  function handleOpenPool(poolId) {
    navigate(`/mlb-playoff/current/${poolId}`);
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white" overflowX="hidden">
      <PageHeader
        title="MLB PLAYOFF LOBBY"
        onBack={() => navigate("/")}
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="container.xl" py={10}>
        <VStack align="stretch" spacing={8}>
          <VStack align="start" spacing={2}>
            <Text fontSize="lg" color="whiteAlpha.900">
              Welcome to the MLB Playoff Challenge lobby. Enter your current game, view open pools, or start a new pool.
            </Text>
            {poolCreationOpen && season && (
              <Alert status="success" borderRadius="md" mt={1} color="green.900">
                <AlertIcon flexShrink={0} />
                <Text ml={2}>
                  The <strong>{season.name}</strong> season is available for set up — create your pool below!
                </Text>
              </Alert>
            )}
          </VStack>

          {/* My Pools + Join row */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {/* Your Active Games */}
            <Box
              bg="white"
              color="black"
              border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg"
              boxShadow="lg"
              p={8}
              minH="260px"
              minW="0"
              position="relative"
              overflow="hidden"
            
              opacity={!loadingPools && myPools.length === 0 ? 0.5 : 1}
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
                opacity={0.06}
                pointerEvents="none"
                zIndex={0}
              >
                <img src={mlbPlayoffLogo} alt="" style={{ width: "500px" }} />
              </Box>

              <VStack align="stretch" spacing={4} h="100%" position="relative" zIndex={1}>
                <Heading size="md" color={TOP_BAR_BLUE}>Your Active Games</Heading>
                <Text color="gray.700">Pools you belong to for the current MLB Playoffs.</Text>

                {loadingPools ? (
                  <Flex flex="1" align="center" justify="center"><Spinner /></Flex>
                ) : myPools.length === 0 ? (
                  <Flex flex="1" align="center" justify="center">
                    <Text fontSize="lg" color="gray.500" textAlign="center">
                      You are not currently in any pools.
                    </Text>
                  </Flex>
                ) : (
                  <Box maxH={{ base: "240px", sm: "unset" }} overflowY={{ base: "auto", sm: "visible" }} mt={1}>
                    {/* Mobile: compact list */}
                    <VStack align="stretch" spacing={0} display={{ base: "flex", sm: "none" }}>
                      {myPools.map((pool) => (
                        <Flex key={pool.id} align="center" py={2} borderBottom="1px solid" borderColor="gray.100">
                          <Text fontWeight="semibold" fontSize="sm" flex="1" mr={3} noOfLines={1}>{pool.name}</Text>
                          <HStack spacing={3} flexShrink={0}>
                            {(pool.owner_user_id === user?.id || pool.current_user_is_co_admin) && (
                              <Button variant="link" size="sm" color={TOP_BAR_BLUE} onClick={() => navigate(`/mlb-playoff/setup/${pool.id}`)}>Edit</Button>
                            )}
                            <Button variant="link" size="sm" color={TOP_BAR_BLUE} fontWeight="bold" onClick={() => handleOpenPool(pool.id)}>Open</Button>
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                    {/* Desktop: card grid */}
                    <PoolCardScroller display={{ base: "none", sm: "block" }}>
                      {myPools.map((pool) => (
                        <Box key={pool.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="gray.50">
                          <VStack align="center" spacing={2}>
                            <Text fontWeight="bold" textAlign="center">{pool.name}</Text>
                            <Text fontSize="sm" color="gray.600" textAlign="center">Participants: {pool.user_count}</Text>
                            {pool.total_pot != null && (
                              <Text fontSize="sm" color="gray.600" textAlign="center">Total Pot: ${pool.total_pot}</Text>
                            )}
                            <HStack spacing={2} justify="center">
                              {(pool.owner_user_id === user?.id || pool.current_user_is_co_admin) && (
                                <Button size="sm" variant="outline" borderColor={TOP_BAR_BLUE} color={TOP_BAR_BLUE} onClick={() => navigate(`/mlb-playoff/setup/${pool.id}`)} _hover={{ bg: "gray.100" }}>Edit Pool</Button>
                              )}
                              <Button size="sm" bg={TOP_BAR_BLUE} color="white" onClick={() => handleOpenPool(pool.id)} _hover={{ bg: "#00004a" }}>Open Pool</Button>
                            </HStack>
                          </VStack>
                        </Box>
                      ))}
                    </PoolCardScroller>
                  </Box>
                )}
              </VStack>
            </Box>

            {/* Join an Open Pool */}
            <Box
              bg="white"
              color="black"
              border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg"
              boxShadow="lg"
              p={8}
              minH="260px"
              minW="0"
              position="relative"
              overflow="hidden"
              opacity={entriesOpen ? 1 : 0.6}
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
                opacity={0.06}
                pointerEvents="none"
                zIndex={0}
              >
                <img src={mlbPlayoffLogo} alt="" style={{ width: "500px" }} />
              </Box>

              <VStack align="stretch" spacing={5} h="100%" position="relative" zIndex={1}>
                <Heading size="md" color={TOP_BAR_BLUE}>Join an Open Pool</Heading>
                <Text color="gray.700">Available pools for the current season.</Text>
                {joinError && (
                  <Alert status="error" borderRadius="md"><AlertIcon />{joinError}</Alert>
                )}
                {loadingPools ? (
                  <Flex flex="1" align="center" justify="center"><Spinner /></Flex>
                ) : joinablePools.length === 0 ? (
                  <Flex flex="1" align="center" justify="center">
                    <Text fontSize="lg" color="gray.500" textAlign="center">
                      No open pools available right now.
                    </Text>
                  </Flex>
                ) : (
                  <Box maxH={{ base: "240px", sm: "unset" }} overflowY={{ base: "auto", sm: "visible" }} mt={1}>
                    {/* Mobile: compact list */}
                    <VStack align="stretch" spacing={0} display={{ base: "flex", sm: "none" }}>
                      {joinablePools.map((pool) => (
                        <Flex key={pool.id} align="center" py={2} borderBottom="1px solid" borderColor="gray.100">
                          <Text fontWeight="semibold" fontSize="sm" flex="1" mr={3} noOfLines={1}>{pool.name}</Text>
                          <Button variant="link" size="sm" color={TOP_BAR_BLUE} fontWeight="bold" isLoading={joiningPoolId === pool.id} onClick={() => handleJoinPool(pool.id)}>Join</Button>
                        </Flex>
                      ))}
                    </VStack>
                    {/* Desktop: card grid */}
                    <PoolCardScroller display={{ base: "none", sm: "block" }}>
                      {joinablePools.map((pool) => (
                        <Box key={pool.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="gray.50">
                          <VStack align="center" spacing={2}>
                            <Text fontWeight="bold" textAlign="center">{pool.name}</Text>
                            <Text fontSize="sm" color="gray.600" textAlign="center">
                              {pool.user_count} participant{pool.user_count !== 1 ? "s" : ""}
                              {pool.total_pot != null ? ` · $${pool.total_pot} pot` : ""}
                            </Text>
                            <Button size="sm" bg={TOP_BAR_BLUE} color="white" _hover={{ bg: "#00004a" }} isLoading={joiningPoolId === pool.id} onClick={() => handleJoinPool(pool.id)}>Join</Button>
                          </VStack>
                        </Box>
                      ))}
                    </PoolCardScroller>
                  </Box>
                )}
              </VStack>
            </Box>
          </SimpleGrid>

          {/* Rules + New Pool */}
          <Box
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={8}
          >
            <VStack align="stretch" spacing={8}>

              {/* Want to Start a New Pool? */}
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>Want to Start a New Pool?</Heading>
                {seasonStarted && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    New games can&apos;t be created mid playoffs
                  </Alert>
                )}
              </Box>

              <Divider borderColor="gray.300" />

              {/* Rules */}
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>Rules</Heading>
                <UnorderedList spacing={3} pl={5}>
                  <ListItem>
                    Pick <strong>six hitters</strong> from any clinched playoff team.
                  </ListItem>
                  <ListItem>
                    Once a team plays their <strong>first playoff game</strong>, those players are locked — they cannot be included in new entries.
                  </ListItem>
                  <ListItem>
                    You may submit <strong>more than one entry</strong> per pool, each with a unique team name.
                  </ListItem>
                </UnorderedList>
              </Box>

              <Divider borderColor="gray.300" />

              {/* Scoring */}
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>Points Structure</Heading>
                <UnorderedList spacing={3} pl={5}>
                  <ListItem>Your score is the <strong>total bases</strong> accumulated by your six players throughout the entire playoffs.</ListItem>
                  <ListItem>
                    <strong>Total Bases:</strong>
                    <UnorderedList mt={1} spacing={1}>
                      <ListItem>Single = 1</ListItem>
                      <ListItem>Double = 2</ListItem>
                      <ListItem>Triple = 3</ListItem>
                      <ListItem>Home Run = 4</ListItem>
                      <ListItem>Walk / Out / Error / HBP = 0</ListItem>
                    </UnorderedList>
                  </ListItem>
                  <ListItem>The leaderboard updates each morning as stats are pulled from MLB.</ListItem>
                  <ListItem>Tiebreaker determined by pool admin.</ListItem>
                </UnorderedList>
              </Box>

              <Divider borderColor="gray.300" />

              {/* Pool creation form — always shown, disabled when not open */}
              <Box opacity={poolCreationOpen ? 1 : 0.5} pointerEvents={poolCreationOpen ? "auto" : "none"}>
                {createError && poolCreationOpen && (
                  <Alert status="error" borderRadius="md" mb={4}><AlertIcon />{createError}</Alert>
                )}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                  <FormControl isRequired>
                    <FormLabel>Pool Name</FormLabel>
                    <Input
                      value={newPoolName}
                      onChange={(e) => { setNewPoolName(e.target.value); setCreateError(""); }}
                      placeholder="Enter pool name"
                      isDisabled={!poolCreationOpen}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Pool Depth ($)</FormLabel>
                    <Input
                      type="number"
                      min={1}
                      value={newPoolDepth}
                      onChange={(e) => setNewPoolDepth(e.target.value)}
                      placeholder="Optional buy-in amount"
                      isDisabled={!poolCreationOpen}
                    />
                  </FormControl>
                </SimpleGrid>
                {poolCreationOpen && (
                  <Button
                    bg={TOP_BAR_BLUE}
                    color="white"
                    _hover={{ bg: "#00004a" }}
                    isLoading={creating}
                    loadingText="Creating..."
                    onClick={handleCreatePool}
                  >
                    Start New Pool
                  </Button>
                )}
              </Box>

              {!poolCreationOpen && !seasonStarted && (
                <Text color="gray.500" fontSize="sm">
                  Once next season setup is available, this page will allow users to create a
                  new pool before the playoffs begin.
                </Text>
              )}

            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
